"use client";

import React from "react";
import { FileText, Landmark, MessagesSquare, Layers, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const trustPillars = [{
  id: 1,
  title: "Written Scope & Planning",
  description: "Clear architectural scopes, realistic timeline milestones, and itemized material schedules established before work begins.",
  icon: FileText,
  badgeText: "Detailed Scopes"
}, {
  id: 2,
  title: "Permitting & Code Coordination",
  description: "Direct coordination with local municipal building departments to submit documentation and organize required stage reviews.",
  icon: Landmark,
  badgeText: "Local Process"
}, {
  id: 3,
  title: "Clear, Continuous Communication",
  description: "Regular milestone updates, scheduled progress reviews, and open communication channels throughout the duration of your project.",
  icon: MessagesSquare,
  badgeText: "Direct Updates"
}, {
  id: 4,
  title: "Phased Milestone Walkthroughs",
  description: "Structured walkthroughs at each critical phase—framing, rough-ins, and final trim—to confirm alignment with project drawings.",
  icon: Layers,
  badgeText: "Quality Reviews"
}];
export default function Standards() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-us" data-nav="light" className="bg-background text-foreground relative py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 max-w-3xl sm:mb-16">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="bg-secondary text-secondary-foreground border-border rounded px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border">
              Our Standards
            </span>
          </div>

          <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Built with precision, guided by structured process.
          </h2>

          <p className="text-muted-foreground mt-4 text-base leading-relaxed sm:text-lg">
            Every renovation is guided by deliberate planning, local municipal
            coordination, and attentive craftsmanship. We support your project from initial concept to final trim.
          </p>
        </div>

        {/* Asymmetrical 5/7 Grid Layout */}
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Left Column: 4 Frosted Trust Cards (7-col width on desktop) */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7 lg:gap-6">
            {trustPillars.map((pillar, i) => {
            const IconComponent = pillar.icon;
            return <motion.div key={pillar.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
              y: -4,
              transition: {
                duration: 0.3,
                ease: "easeOut"
              }
            }} className="group">
                  <Card className="border-border bg-card/85 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-border hover:shadow-md backdrop-blur-md h-full rounded-xl border p-0">
                    <CardContent className="flex h-full flex-col justify-between p-6 sm:p-7">
                      <div>
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <div className="bg-secondary text-foreground flex size-11 shrink-0 items-center justify-center rounded-lg border border-border">
                            <IconComponent className="size-5" />
                          </div>
                          <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-border text-[11px] font-medium tracking-wide border">
                            {pillar.badgeText}
                          </Badge>
                        </div>

                        <h3 className="font-serif text-lg font-semibold tracking-tight sm:text-xl text-card-foreground">
                          {pillar.title}
                        </h3>

                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Column: Editorial Visual + Credential Overlays (5-col width on desktop) */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative lg:col-span-5 lg:sticky lg:top-28">
            <div className="border-border bg-muted/40 relative overflow-hidden rounded-2xl border p-2 shadow-md">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_craftsmen_on_site.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_craftsmen_on_site.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_craftsmen_on_site.png-wvc-sizes" alt="Decatur Heritage Renovations team craftsman inspecting fine architectural woodworking in a residential home" loading="lazy" decoding="async" className="h-full w-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                {/* Layered Top Badge */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <div className="bg-background/90 text-foreground border-border rounded-lg border px-3 py-1.5 text-xs font-medium backdrop-blur-md shadow-xs">
                    Residential Remodeling Specialists
                  </div>
                </div>

                {/* Layered Bottom Frosted Card */}
                <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-border bg-card/90 p-5 shadow-lg backdrop-blur-md">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Locally Focused
                      </p>
                      <h4 className="font-serif text-lg font-semibold text-card-foreground">
                        Decatur, Oakhurst & Winnona Park
                      </h4>
                      <p className="text-muted-foreground mt-1 text-xs leading-normal">
                        Thoughtful residential remodeling across historic homes, bungalows, and modern additions.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                    <span className="text-xs font-medium text-muted-foreground">
                      Ready to discuss your project?
                    </span>
                    <Button asChild size="sm" variant="default" className="rounded-md">
                      <Link to="/ContactPage" className="inline-flex items-center gap-1.5">
                        <span>Get in Touch</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}